import { useCallback, useId, useState, type CSSProperties, type FormEvent, type ReactElement } from "react";
import { color, radius, space } from "@tokens/index";
import { Badge, Button, Stack, Text, TextInput } from "@primitives/index";
import { AdminSection, AdminSelect, Field, FormActions, MutationError } from "../admin-ui";
import type { QueueSummary } from "../queues";
import { taskTypeLabel } from "../workflows";
import type { TaskTypeOption } from "../workflows";
import { PredicateEditor } from "./PredicateEditor";
import type { RuleFormState } from "./ruleForm";

// Single-select against the caller's queues. First-hop ingest always
// lands work items in exactly one queue — multi-queue fan-out is a
// distinct, user-driven operation (broadcast / attach), not something
// expressed by authoring a multi-target rule. We deliberately do not
// show every queue in the org; the admin lists their own queues +
// categories, which is enough for the initial UX.
interface QueuePickerProps {
    readonly availableQueues: readonly QueueSummary[];
    readonly selectedQueueId: string;
    readonly onChange: (next: string) => void;
    readonly labelledBy: string;
}

interface QueueChipProps {
    readonly queue: QueueSummary;
    readonly selected: boolean;
    readonly onSelect: () => void;
}

function QueueChip({ queue, selected, onSelect }: QueueChipProps): ReactElement {
    const [hovered, setHovered] = useState(false);
    const disabled = !queue.isWorkable;
    const style: CSSProperties = {
        display: "inline-flex",
        alignItems: "center",
        gap: space[1],
        minHeight: 32,
        maxWidth: "100%",
        paddingInline: space[3],
        paddingBlock: space[1],
        borderRadius: radius.pill,
        border: `1px solid ${selected ? color.border.accent : color.border.subtle}`,
        background: selected ? color.bg.selected : hovered && !disabled ? color.bg.hover : color.bg.surface,
        color: selected ? color.fg.default : color.fg.muted,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.56 : 1,
        appearance: "none",
    };
    return (
        <button
            type="button"
            aria-pressed={selected}
            data-hit-area=""
            style={style}
            onClick={onSelect}
            disabled={disabled}
            title={queue.isWorkable ? queue.name : `${queue.name} (category — not routable)`}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <Text role="bodySm" weight="medium" truncate style={{ color: "inherit" }}>
                {queue.name}
            </Text>
        </button>
    );
}

function QueuePicker({
    availableQueues,
    selectedQueueId,
    onChange,
    labelledBy,
}: QueuePickerProps): ReactElement {
    const activeQueues = availableQueues.filter((queue) => queue.status === "active");

    if (activeQueues.length === 0) {
        return (
            <Text role="caption" tone="muted">
                No queues available. Create a queue first, then come back here.
            </Text>
        );
    }

    return (
        <div
            role="group"
            aria-labelledby={labelledBy}
            style={{ display: "flex", flexWrap: "wrap", gap: space[2] }}
        >
            {activeQueues.map((q) => (
                <QueueChip
                    key={q.id}
                    queue={q}
                    selected={selectedQueueId === q.id}
                    onSelect={() => onChange(q.id)}
                />
            ))}
        </div>
    );
}

interface RuleEditorProps {
    readonly mode: "create" | "edit";
    readonly form: RuleFormState;
    readonly onChange: (next: RuleFormState) => void;
    readonly availableKinds: readonly string[];
    readonly availableTaskTypes: readonly TaskTypeOption[];
    readonly availableTagKeys: readonly string[];
    readonly availableQueues: readonly QueueSummary[];
    readonly isSubmitting: boolean;
    readonly submitError: Error | null;
    readonly onSubmit: () => void;
    readonly onCancel: () => void;
    // When set, the editor is opened from a queue's detail page. The
    // target picker is hidden and the form's targetQueueId is treated
    // as pinned to this queue. The caller is responsible for seeding
    // the form with the matching id; we render a read-only badge and
    // a short explanation so the admin understands cross-queue
    // retargeting is intentionally a master-page concern.
    readonly lockedTargetQueueId?: string;
}

export function RuleEditor({
    mode,
    form,
    onChange,
    availableKinds,
    availableTaskTypes,
    availableTagKeys,
    availableQueues,
    isSubmitting,
    submitError,
    onSubmit,
    onCancel,
    lockedTargetQueueId,
}: RuleEditorProps): ReactElement {
    const predicatesLabelId = useId();
    const targetLabelId = useId();

    const handleSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            onSubmit();
        },
        [onSubmit],
    );

    const isValid =
        form.workflowKind.trim().length > 0 &&
        form.description.trim().length > 0 &&
        form.targetQueueId.length > 0 &&
        form.predicates.every((p) => p.key.length > 0 && p.value.length > 0);

    const lockedQueueName =
        lockedTargetQueueId !== undefined
            ? (availableQueues.find((q) => q.id === lockedTargetQueueId)?.name ??
                lockedTargetQueueId)
            : null;

    return (
        <AdminSection
            title={mode === "create" ? "New routing rule" : "Edit routing rule"}
            description="Predicates use AND semantics — every key must match for the rule to fire."
        >
            <form onSubmit={handleSubmit}>
                <Stack direction="column" gap={5}>
                    <Field
                        label="Workflow kind"
                        hint="Workflow kind is immutable after creation; it determines the rule's storage partition."
                    >
                        {(id) => (
                            <AdminSelect
                                id={id}
                                value={form.workflowKind}
                                disabled={mode === "edit"}
                                onChange={(e) => onChange({ ...form, workflowKind: e.target.value })}
                            >
                                <option value="" disabled>
                                    {availableKinds.length === 0
                                        ? "Loading workflow kinds…"
                                        : "Select a workflow kind"}
                                </option>
                                {availableKinds.map((k) => (
                                    <option key={k} value={k}>
                                        {k}
                                    </option>
                                ))}
                                {/*
                                    Edge case: an edit form may carry a kind that
                                    was retired from the registry between rule
                                    creation and now. Render it as a stable option
                                    so the form stays valid; the disabled select
                                    prevents the admin from picking anything else.
                                */}
                                {form.workflowKind.length > 0 &&
                                    !availableKinds.includes(form.workflowKind) && (
                                        <option value={form.workflowKind}>
                                            {form.workflowKind} (unregistered)
                                        </option>
                                    )}
                            </AdminSelect>
                        )}
                    </Field>

                    <Field label="Entity type (optional)">
                        {(id) => (
                            <TextInput
                                id={id}
                                fullWidth
                                placeholder="e.g. order, shipment — leave blank to match any"
                                value={form.entityType}
                                onChange={(e) => onChange({ ...form, entityType: e.target.value })}
                            />
                        )}
                    </Field>

                    <Field
                        label="Task type (optional)"
                        hint="Route every task of a given type to this queue. Messages and notes never match a task-scoped rule. Leave blank to match any task type and disambiguate with tag predicates instead."
                    >
                        {(id) => (
                            <AdminSelect
                                id={id}
                                value={form.taskType}
                                onChange={(e) => onChange({ ...form, taskType: e.target.value })}
                            >
                                <option value="">Any task type</option>
                                {availableTaskTypes.map((t) => (
                                    <option key={t.id} value={t.id}>
                                        {t.label}
                                    </option>
                                ))}
                                {/*
                                    Edit-time defence: a stored rule may
                                    carry a TaskType that's been retired from
                                    the registry between authoring and now.
                                    Render it as a stable option so the form
                                    stays valid and an admin can re-pick a
                                    live value without the previous selection
                                    silently disappearing.
                                */}
                                {form.taskType.length > 0 &&
                                    !availableTaskTypes.some((t) => t.id === form.taskType) && (
                                        <option value={form.taskType}>
                                            {taskTypeLabel(form.taskType)} (unregistered)
                                        </option>
                                    )}
                            </AdminSelect>
                        )}
                    </Field>

                    <Field label="Description">
                        {(id) => (
                            <TextInput
                                id={id}
                                fullWidth
                                placeholder="What this rule does, in one sentence"
                                value={form.description}
                                onChange={(e) => onChange({ ...form, description: e.target.value })}
                            />
                        )}
                    </Field>

                    <div
                        role="group"
                        aria-labelledby={predicatesLabelId}
                        style={{ display: "grid", gap: space[1], minWidth: 0 }}
                    >
                        <Text as="p" id={predicatesLabelId} role="bodySm" weight="medium" tone="muted">
                            Tag predicates
                        </Text>
                        <PredicateEditor
                            predicates={form.predicates}
                            availableKeys={availableTagKeys}
                            onChange={(next) => onChange({ ...form, predicates: next })}
                        />
                    </div>

                    <div style={{ display: "grid", gap: space[1], minWidth: 0 }}>
                        <Text as="p" id={targetLabelId} role="bodySm" weight="medium" tone="muted">
                            Target queue
                        </Text>
                        {lockedTargetQueueId !== undefined ? (
                            <Stack direction="column" gap={1}>
                                <div style={{ display: "flex", gap: space[2], flexWrap: "wrap" }}>
                                    <Badge variant="neutral" size="sm" caps={false}>
                                        {lockedQueueName}
                                    </Badge>
                                </div>
                                <Text role="caption" tone="muted">
                                    Target locked to this queue. Retargeting is available from the routing-rules master page.
                                </Text>
                            </Stack>
                        ) : (
                            <Stack direction="column" gap={2}>
                                <Text role="caption" tone="muted">
                                    Each rule routes matching items to exactly one queue. Multi-queue
                                    fan-out is a per-item user action, not a rule property.
                                </Text>
                                <QueuePicker
                                    availableQueues={availableQueues}
                                    selectedQueueId={form.targetQueueId}
                                    onChange={(next) => onChange({ ...form, targetQueueId: next })}
                                    labelledBy={targetLabelId}
                                />
                            </Stack>
                        )}
                    </div>

                    <MutationError error={submitError} />

                    <FormActions
                        primary={
                            <Button
                                type="submit"
                                variant="primary"
                                size="md"
                                disabled={!isValid}
                                busy={isSubmitting}
                            >
                                {mode === "create" ? "Create rule" : "Save changes"}
                            </Button>
                        }
                        secondary={
                            <Button
                                type="button"
                                variant="ghost"
                                size="md"
                                onClick={onCancel}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                        }
                    />
                </Stack>
            </form>
        </AdminSection>
    );
}
